import React, { useState, useEffect } from 'react'
import Layout from '../Home'
import http from '../../services/httpServiceEver'
import { KeyOutlined, CopyOutlined } from '@ant-design/icons'
import { Table, Spin, Input, Avatar, Popconfirm, Button, Modal, Typography, message } from 'antd'
import styled from 'styled-components'

const { Search: SearchAnt } = Input
const { Text: AntText, Paragraph } = Typography

const Text = styled.p`
  font-weight: bold;
  color: ${(props) => props.theme.primaryText};
`

const Search = styled(SearchAnt)`
  i {
    color: ${(props) => props.theme.primaryButton};
  }
`

const HeaderWrapper = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin: 20px 0px;
  i {
    margin-right: 15px;
  }
`

const Container = styled.div`
  width: 100%;
  padding: 20px;
`

const PasswordBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 12px 16px;
  margin-top: 12px;
`

export const AdminDoctorPassword = (props) => {
  const [doctors, setDoctors] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [generatingId, setGeneratingId] = useState(null)
  const [resultModal, setResultModal] = useState({ visible: false, doctor: null, password: '' })

  useEffect(() => {
    fetchDoctors()
  }, [])

  // Fetch every doctor across all doctor types into a single flat list.
  const fetchDoctors = async () => {
    setLoading(true)
    try {
      const responseDoctorType = await http.get('doctorTypes')
      const types = responseDoctorType.data || []
      const appUserResponses = await Promise.all(
        types.map((type) =>
          http
            .get(`DoctorTypes/${type.id}/appUsers?filter[order]=employeeId`)
            .then((res) => res.data || [])
            .catch(() => []),
        ),
      )
      const merged = []
      const seen = new Set()
      appUserResponses.flat().forEach((user) => {
        if (user && !seen.has(user.id)) {
          seen.add(user.id)
          merged.push(user)
        }
      })
      setDoctors(merged)
    } catch (error) {
      console.error('Error fetching doctors:', error)
      message.error('เกิดข้อผิดพลาดในการโหลดรายชื่อแพทย์')
    } finally {
      setLoading(false)
    }
  }

  const onGeneratePassword = async (doctor) => {
    setGeneratingId(doctor.id)
    try {
      const response = await http.patch(`users/${doctor.id}/generateNewPassword`)
      const password = response.data?.password
      if (!password) {
        throw new Error('No password returned')
      }
      setResultModal({ visible: true, doctor, password })
    } catch (error) {
      console.error('Error generating password:', error)
      message.error('เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน')
    } finally {
      setGeneratingId(null)
    }
  }

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(resultModal.password)
      message.success('คัดลอกรหัสผ่านแล้ว')
    } catch (e) {
      message.error('ไม่สามารถคัดลอกได้ กรุณาคัดลอกด้วยตนเอง')
    }
  }

  const columns = [
    {
      title: 'ภาพโปรไฟล์',
      dataIndex: 'profileImage',
      key: 'profileImage',
      render: (text) => <Avatar src={text} alt="Profile Image" />,
    },
    {
      title: 'ชื่อ',
      dataIndex: 'fullname',
      key: 'fullname',
    },
    {
      title: 'อีเมล',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'เชี่ยวชาญ',
      dataIndex: ['detail', 'specialist'],
      key: 'specialist',
    },
    {
      title: 'แผนก',
      dataIndex: ['detail', 'department'],
      key: 'department',
    },
    {
      title: 'คำสั่ง',
      key: 'action',
      render: (record) => (
        <Popconfirm
          title={`ต้องการสุ่มรหัสผ่านใหม่ให้ "${record.fullname}" ใช่หรือไม่? รหัสเดิมจะใช้ไม่ได้ทันที`}
          onConfirm={() => onGeneratePassword(record)}
          okText="ใช่"
          cancelText="ไม่"
        >
          <Button type="primary" icon={<KeyOutlined />} loading={generatingId === record.id}>
            เปลี่ยนรหัสผ่าน
          </Button>
        </Popconfirm>
      ),
    },
  ]

  const filteredDoctors = doctors.filter(
    (user) =>
      (user.fullname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()),
  )

  return (
    <Layout
      {...props}
      render={(
        props, //eslint-disable-line
      ) => (
        <Container>
          <HeaderWrapper>
            <KeyOutlined />
            <Text bold>เปลี่ยนรหัสผ่านแพทย์ (Admin)</Text>
            <Search
              placeholder="ค้นหา..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 200, marginLeft: 'auto' }}
            />
          </HeaderWrapper>
          {loading ? (
            <Spin size="large" />
          ) : (
            <Table dataSource={filteredDoctors} columns={columns} rowKey="id" />
          )}

          <Modal
            title="รหัสผ่านใหม่"
            visible={resultModal.visible}
            onCancel={() => setResultModal({ visible: false, doctor: null, password: '' })}
            footer={[
              <Button key="close" onClick={() => setResultModal({ visible: false, doctor: null, password: '' })}>
                ปิด
              </Button>,
            ]}
          >
            {resultModal.doctor && (
              <>
                <Paragraph>
                  สร้างรหัสผ่านใหม่ให้ <AntText strong>{resultModal.doctor.fullname}</AntText> (
                  {resultModal.doctor.email}) เรียบร้อยแล้ว
                </Paragraph>
                <Paragraph type="warning">
                  กรุณาคัดลอกและส่งให้แพทย์ทันที รหัสนี้จะแสดงเพียงครั้งเดียวเท่านั้น
                </Paragraph>
                <PasswordBox>
                  <AntText code style={{ fontSize: 16 }}>
                    {resultModal.password}
                  </AntText>
                  <Button type="link" icon={<CopyOutlined />} onClick={copyPassword}>
                    คัดลอก
                  </Button>
                </PasswordBox>
              </>
            )}
          </Modal>
        </Container>
      )}
    />
  )
}

export default AdminDoctorPassword
